import React, { useEffect, useMemo, useRef, useState } from "react";
import "./Listings.css";
import HotelListings from "./HotelListings";
import GuideListings from "./GuideListings";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

function CountUp({ to = 0, duration = 800, prefix = "" }) {
  const [val, setVal] = useState(0);
  const start = useRef(null);
  const target = Number(to || 0);

  useEffect(() => {
    start.current = null;
    let raf = 0;
    const step = (ts) => {
      if (!start.current) start.current = ts;
      const p = Math.min(1, (ts - start.current) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(target * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);

  return <>{prefix}{Math.round(val).toLocaleString()}</>;
}

export default function Listings() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [view, setView] = useState("all");
  const [cards, setCards] = useState({
    totalListings: 0,
    pendingListings: 0,
    activeListings: 0,
    hotelListings: 0,
    guideListings: 0,
    deletedListings: 0,
  });

  const cardList = useMemo(() => ([
    { title: "Available all Listings", value: cards.totalListings, sub: "All Hotel and Guide Listings", icon: "📄", prefix: "+" },
    { title: "Active Listings", value: cards.activeListings, sub: "Published Hotels and Guides", icon: "✅", prefix: "+" },
    { title: "Pending Listings", value: cards.pendingListings, sub: "Pending Hotels and Guides", icon: "⏳", prefix: "+" },
    { title: "Available Hotel Listings", value: cards.hotelListings, sub: "Hotel Listings", icon: "🏨", prefix: "+" },
    { title: "Available Guide Listings", value: cards.guideListings, sub: "Guide Listings", icon: "🧭", prefix: "+" },
    { title: "Deleted Listings", value: cards.deletedListings, sub: "Soft-deleted Hotels and Guides", icon: "🗑️", prefix: "+" },
  ]), [cards]);

  const handleStatusChange = (summary) => {
    if (summary) {
      setCards(prev => ({ ...prev, ...summary }));
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API}/admin/listings/summary`);
        const json = await res.json();
        if (!res.ok) throw new Error(json?.message || "Failed to fetch summary.");
        if (mounted) {
          setCards({
            totalListings: Number(json.totalListings) || 0,
            activeListings: Number(json.activeListings) || 0,
            pendingListings: Number(json.pendingListings) || 0,
            hotelListings: Number(json.hotelListings) || 0,
            guideListings: Number(json.guideListings) || 0,
            deletedListings: Number(json.deletedListings) || 0,
          });
        }
      } catch (e) {
        if (mounted) setErr(e.message || "Load failed");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <div className="ls-page">
        <div className="ls-head">
          <div>
            <h2 className="ls-title">Hotel and Guide Listings</h2>
            <p className="muted">Loading…</p>
          </div>
        </div>
        <section className="ls-cards">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="card stat" style={{ opacity: 0.6 }}>
              <div className="stat-top">
                <div className="stat-title">Loading…</div>
                <div className="lock" aria-hidden>…</div>
              </div>
              <div className="stat-value">—</div>
              <div className="stat-sub">—</div>
            </div>
          ))}
        </section>
      </div>
    );
  }

  if (err) {
    return (
      <div className="ls-page">
        <div className="ls-head">
          <div>
            <h2 className="ls-title">Hotel and Guide Listings</h2>
            <p className="muted" style={{ color: "crimson" }}>{err}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ls-page">
      <div className="ls-head">
        <div>
          <h2 className="ls-title">Hotel and Guide Listings</h2>
          <p className="muted">Manage all listings on the platform</p>
        </div>
      </div>

      <section className="ls-cards">
        {cardList.map((s, i) => (
          <div key={i} className="card stat">
            <div className="stat-top">
              <div className="stat-title">{s.title}</div>
              <div className="lock" aria-hidden>{s.icon}</div>
            </div>
            <div className="stat-value"><CountUp to={s.value} prefix={s.prefix || ""} /></div>
            <div className="stat-sub">{s.sub}</div>
          </div>
        ))}
      </section>

      <div className="ls-toolbar">
        <div className="segmented">
          <button className={`seg ${view === "all" ? "active" : ""}`} onClick={() => setView("all")}>All</button>
          <button className={`seg ${view === "hotel" ? "active" : ""}`} onClick={() => setView("hotel")}>Hotel</button>
          <button className={`seg ${view === "guide" ? "active" : ""}`} onClick={() => setView("guide")}>Guide</button>
        </div>
      </div>

      {(view === "all" || view === "hotel") && <HotelListings onStatusChange={handleStatusChange} />}
      {(view === "all" || view === "guide") && <GuideListings onStatusChange={handleStatusChange} />}
    </div>
  );
}
